import { Trans, t } from "@lingui/macro";
import { useCallback, useState } from "react";

import Modal from "components/Modal/Modal";
import { registerAsTrader } from "domain/copyTrading/contracts";
import { useChainId } from "lib/chains";
import { usePendingTxns } from "lib/usePendingTxns";
import { useEthersSigner } from "lib/wallets/useEthersSigner";

type Props = {
  isVisible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

const MAX_FEE_BPS = 1000; // 10%

export function RegisterTraderModal({ isVisible, onClose, onSuccess }: Props) {
  const { chainId } = useChainId();
  const signer = useEthersSigner({ chainId });
  const [, setPendingTxns] = usePendingTxns();

  const [feeInput, setFeeInput] = useState("1.0"); // default 1%
  const [isSubmitting, setIsSubmitting] = useState(false);

  const feePct = parseFloat(feeInput) || 0;
  const feeBps = Math.round(feePct * 100);
  const isValid = feePct >= 0 && feeBps <= MAX_FEE_BPS;

  const handleConfirm = useCallback(async () => {
    if (!signer || !isValid) return;
    setIsSubmitting(true);
    try {
      await registerAsTrader({ chainId, signer, copyFeeRateBps: feeBps, setPendingTxns });
      onSuccess?.();
      onClose();
    } catch {
      // callContract shows error toast
    } finally {
      setIsSubmitting(false);
    }
  }, [signer, chainId, feeBps, isValid, setPendingTxns, onSuccess, onClose]);

  return (
    <Modal
      isVisible={isVisible}
      setIsVisible={onClose}
      label={<Trans>Become a Leader Trader</Trans>}
      className="CT-follow-modal"
      qa="register-trader-modal"
    >
      <div style={{ padding: "0 0 0.8rem" }}>
        <p style={{ color: "var(--color-gray-400)", fontSize: "var(--font-size-body-small)", marginBottom: "2rem", lineHeight: 1.6 }}>
          <Trans>
            Register as a leader trader and let others automatically copy your positions. You earn a fee on every
            trade copied from you.
          </Trans>
        </p>

        {/* Fee rate input */}
        <div className="CT-modal-field">
          <label className="CT-modal-field__label">
            <Trans>Copy fee</Trans>
          </label>
          <div className="CT-modal-input-wrap">
            <input
              className="CT-modal-input"
              type="number"
              min="0"
              max="10"
              step="0.1"
              value={feeInput}
              onChange={(e) => setFeeInput(e.target.value)}
              placeholder="1.0"
            />
            <span className="CT-modal-input-token">%</span>
          </div>
          <p className="CT-modal-field__hint">
            <Trans>
              Charged on each follower's collateral when your trade is copied. Max 10%. Set to 0 for free.
            </Trans>
          </p>
          {feeBps > MAX_FEE_BPS && (
            <p style={{ color: "var(--color-red-400)", fontSize: "1.1rem", marginTop: "0.4rem" }}>
              <Trans>Maximum copy fee is 10%</Trans>
            </p>
          )}
        </div>

        {/* Fee preview */}
        <div className="CT-modal-fees">
          <div className="CT-modal-row">
            <span className="CT-modal-row__label"><Trans>Fee rate</Trans></span>
            <span className="CT-modal-row__value">
              {feeBps === 0 ? t`Free` : `${feePct.toFixed(1)}%`}
            </span>
          </div>
          <div className="CT-modal-row">
            <span className="CT-modal-row__label"><Trans>Example — follower trades $500</Trans></span>
            <span className="CT-modal-row__value CT-positive">
              +${((500 * feeBps) / 10000).toFixed(2)} BUSDC
            </span>
          </div>
        </div>

        <div className="CT-modal-notice">
          <span className="CT-modal-notice__icon">ℹ</span>
          <p>
            <Trans>
              You can update your fee rate and follower cap at any time from your trader dashboard. Fees accumulate
              on-chain and can be claimed whenever you like.
            </Trans>
          </p>
        </div>

        <button
          className="CT-btn-primary w-full mt-16"
          onClick={handleConfirm}
          disabled={isSubmitting || !isValid || !signer}
        >
          {isSubmitting ? <Trans>Registering…</Trans> : <Trans>Register as Leader</Trans>}
        </button>
      </div>
    </Modal>
  );
}
